// libs
import React, { useEffect, useState } from 'react';
import { styled } from '@nykaa/ui-components';

// Styled Components
const ToastContainer = styled.div<{ hide: boolean; }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.surfaceInverse};
  border-radius: ${({ theme }) => theme.borders.radius10};
  position: fixed;
  bottom: 80px;
  left: 16px;
  right: 16px;
  max-width: 360px;
  margin: 0 auto;
  z-index: 9999;
  color: ${({ theme }) => theme.colors.textInversePrimary};
  font-size: 14px;
  animation: ${({ hide }) => (hide ? 'slideOut' : 'slideIn')} 0.3s ease-in-out forwards;

  @keyframes slideIn {
    from {
      transform: translateY(200%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideOut {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(200%);
      opacity: 0;
    }
  }
`;

const LogoImage = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const Message = styled.span`
  flex: 1;
`;

const ActionButton = styled.button`
  background: transparent;
  border: 0;
  color: ${({ theme }) => theme.colors.surface};
  font-weight: 600;
  font-size: 14px;
  padding: 0;
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
`;

export interface ToastWithLogoProps {
    message: string;
    logoUrl: string;
    actionText: string;
    onActionClick: () => void;
    show: boolean;
    autoHideDuration?: number;
    onHide?: () => void;
}

function ToastWithLogo({
    message,
    logoUrl,
    actionText,
    onActionClick,
    show,
    autoHideDuration = 5000,
    onHide,
}: ToastWithLogoProps) {
    const [hide, setHide] = useState(false);

    useEffect(() => {
        if (show) {
            setHide(false);

            // Auto-hide after specified duration
            const timer = setTimeout(() => {
                setHide(true);
                setTimeout(() => {
                    onHide?.();
                }, 300); // Wait for animation
            }, autoHideDuration);

            return () => clearTimeout(timer);
        }
    }, [show, autoHideDuration, onHide]);

    const handleActionClick = () => {
        setHide(true);
        setTimeout(() => {
            onActionClick();
        }, 300);
    };

    if (!show) {
        return null;
    }

    return (
        <ToastContainer hide={hide}>
            <LogoImage src={logoUrl} alt="Logo" />
            <Message>{message}</Message>
            <ActionButton onClick={handleActionClick}>{actionText}</ActionButton>
        </ToastContainer>
    );
}

export default ToastWithLogo;
